import { axiom } from '../lib/axiom';
import { useState, RefObject } from 'react';

/** Node data for edge drawing */
interface Node {
  id: string;
  x: number;
  y: number;
}

/** Edge data structure */
interface Edge {
  from: string;
  to: string;
  label?: string;
}

/** Drag edge preview state */
interface DragEdge {
  fromNodeId: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

/**
 * Custom hook for managing edge creation via drag-and-drop.
 * Handles visual preview and connection creation between nodes.
 */
export function useEdgeDrawing(
  graphRef: RefObject<HTMLDivElement>,
  nodes: Node[],
  edges: Edge[],
  scale: number,
  panOffset: { x: number; y: number },
  streamId: string,
  createEdge: (streamId: string, fromId: string, toId: string, label?: string) => Promise<unknown>
) {
  const [dragEdge, setDragEdge] = useState<DragEdge | null>(null);
  const [isCreatingEdge, setIsCreatingEdge] = useState(false);

  /**
   * Start edge drag from a node's connection handle.
   * Calculates initial position from node center-right.
   */
  const handleEdgeDragStart = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const node = nodes.find(n => n.id === nodeId);
    if (!node || !graphRef.current) return;

    // Get the connection point position (right side of node)
    const fromX = node.x + 180; // Node width
    const fromY = node.y + 40;  // Node height / 2

    setDragEdge({
      fromNodeId: nodeId,
      fromX,
      fromY,
      toX: fromX,
      toY: fromY,
    });
  };

  /**
   * Update edge preview position as mouse moves.
   * Converts screen coordinates to graph coordinates.
   */
  const handleEdgeDragMove = (e: React.MouseEvent) => {
    if (!dragEdge || !graphRef.current) return;

    const rect = graphRef.current.getBoundingClientRect();
    const toX = (e.clientX - rect.left) / scale - panOffset.x;
    const toY = (e.clientY - rect.top) / scale - panOffset.y;

    setDragEdge({
      ...dragEdge,
      toX,
      toY,
    });
  };

  /**
   * Complete edge creation when dropped on a valid target node.
   * Checks for node overlap and prevents duplicate edges.
   */
  const handleEdgeDragEnd = async (e: React.MouseEvent) => {
    if (!dragEdge || !graphRef.current || !streamId) {
      setDragEdge(null);
      return;
    }

    // Find if we're over a node
    const rect = graphRef.current.getBoundingClientRect();
    const dropX = (e.clientX - rect.left) / scale - panOffset.x;
    const dropY = (e.clientY - rect.top) / scale - panOffset.y;

    // Check if dropped on a node (within node bounds)
    const targetNode = nodes.find(n =>
      n.id !== dragEdge.fromNodeId &&
      dropX >= n.x && dropX <= n.x + 180 &&
      dropY >= n.y && dropY <= n.y + 80
    );

    if (targetNode) {
      // Check if edge already exists
      const edgeExists = edges.some(
        e => e.from === dragEdge.fromNodeId && e.to === targetNode.id
      );

      if (!edgeExists) {
        setIsCreatingEdge(true);
        try {
          await createEdge(streamId, dragEdge.fromNodeId, targetNode.id);
        } catch (err) {
          axiom.error('Failed to create edge', { error: err instanceof Error ? err.message : String(err) });
        } finally {
          setIsCreatingEdge(false);
        }
      }
    }

    setDragEdge(null);
  };

  return {
    dragEdge,
    isCreatingEdge,
    handleEdgeDragStart,
    handleEdgeDragMove,
    handleEdgeDragEnd,
  };
}
